import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTabGroup} from '@angular/material/tabs';
import {Payment} from '../../../../shared/models/account/payment';
import {RequestMethod, RestService} from '../../../../shared/services/rest.service';
import {Actions, ApiEndpoints} from '../../../../shared/constants/ApiEndpoints';
import {ResponseSnackbarService} from '../../../../shared/services/response-snackbar.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Journal} from '../../../../shared/models/account/journal';
import {Invoice} from '../../../../shared/models/account/invoice';
import {TokenService} from '../../../../shared/services/token.service';
import {PageableResponse} from '../../../../shared/models/datasource/pageable-response';
import {Company} from '../../../../shared/models/res/company';
import {environment} from '../../../../../environments/environment';
import {GeneralConstant} from '../../../../shared/constants/GeneralConstant';
import {reactiveFormValidator} from '../../../../shared/utils/Reactive-form-validation-trigger';
import {Subject} from 'rxjs';
import {DecimalPipe} from '@angular/common';
import {PaymentTypeEnum} from '../../../../shared/models/account/PaymentType.enum';
import {MessageInfo} from '../../../../shared/enums/error-messages/MessageInfo';
import {TooltipService} from '../../../../shared/services/tooltip.service';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss']
})
export class PaymentModalComponent implements OnInit {
  /**
   * Tab group
   */
  @ViewChild(MatTabGroup, {static: false}) tabgroup: MatTabGroup;
  /**
   * Form group
   */
  formGroup: FormGroup;
  /**
   * Company data.
   */
  company: Company;

  /**
   * Journal Icons
   */
  bankTransferIcon = '/assets/images/icons/bank-transfer.svg';
  creditCardIcon = '/assets/images/icons/credit-card.svg';
  /**
   * Total amount to pay
   */
  totalAmountToPay: any;
  /**
   * Invoices
   */
  invoices: Invoice[] = [];
  /**
   * Journals
   */
  journals: Journal[] = [];
  /**
   * Payment
   */
  payment: Payment = new Payment();
  /**
   * Invoice
   */
  invoice: Invoice = new Invoice();
  /**
   * User
   */
  user: any;
  /**
   * Is bank transfer
   */
  isBankTransfer: any;
  /**
   * Invoice residual
   */
  invoiceResidual: any;

  /**
   * Invoice draft payment
   */
  invoiceDraftPayment: Payment;

  /**
   * IS stripe
   */
  isStripe: any;

  /**
   * Selected journal
   */
  selectedJournal: any;

  /**
   * Is disabled
   */
  isDisabled = false;

  /**
   * Currency symbol.
   */
  currencySymbolBefore;
  currencySymbolAfter;

  /**
   * Is guest checkout
   */
  guestCheckout: any;

  eventsSubject: Subject<void> = new Subject<void>();
  charChange: any;

  /**
   * Stripe token
   */
  stripeCardToken: any;
  defaultInvalid = MessageInfo.LIMIT_2_AND_100;
  defaultRequired = MessageInfo.REQUIRED_FIELD;
  defaultExists = MessageInfo.ALREADY_EXISTS;
  invalidEmail = MessageInfo.LIMIT_6_AND_100;
  invalidAddress = MessageInfo.LIMIT_1_AND_255;
  invalidZip = MessageInfo.LIMIT_1_AND_10;
  invalidCity = MessageInfo.LIMIT_1_AND_100;
  invalidDesc = MessageInfo.LIMIT_1_AND_2000;
  tooltipService: any;

  protected onDestroy = new Subject<void>();

  /**
   * Constructor
   * @param dialogRef
   * @param data
   * @param restService
   * @param responseSnackBar
   * @param formBuilder
   * @param tokenService
   */
  constructor(
    public dialogRef: MatDialogRef<PaymentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private restService: RestService,
    private responseSnackBar: ResponseSnackbarService,
    private formBuilder: FormBuilder,
    private tokenService: TokenService,
    private dPipe: DecimalPipe) {
  }

  ngOnInit() {
    this.guestCheckout = this.data.guestCheckout ? this.data.guestCheckout : false;
    this.getJournals();
    if (!this.data.guestCheckout) {
      this.user = this.tokenService.getUserFromToken().contact;
      this.getInvoices();
    }
    this.getCompanyInfo();
    if (this.data.paymentId) {
      this.getPaymentData();
    } else if (this.data.invoiceId) {
      this.getInvoiceData();
    } else {
      this.initForm();
    }
  }

  stripeGuestToken(e: string) {
    this.stripeCardToken = e;
  }

  updateStripe() {
    if (this.tabgroup.selectedIndex === 1) {
      this.eventsSubject.next();
    }
  }

  /**
   * Initialize form
   */
  initForm() {
    this.charChange = this.payment.description;
    this.formGroup = this.formBuilder.group({
      invoice: new FormControl(this.fillFormInvoice(), Validators.required),
      journal: new FormControl(this.payment.journal ? this.payment.journal.id : 1, Validators.required),
      description: new FormControl(this.charChange,
        [Validators.maxLength(1000)]),
      ref: new FormControl(this.payment.ref ? this.payment.ref : null,
        [Validators.required, Validators.maxLength(255), Validators.pattern(GeneralConstant.ALPHANUMERIC_AND_SYMBOL_REGEX_VALIDATOR)]),
      amount: new FormControl(this.fillFormAmount() || 1,
        [Validators.required, Validators.min(0.5), Validators.maxLength(16), Validators.pattern(GeneralConstant.DECIMAL_NUMBER_REGEX_VALIDATOR)])
    });
    this.tooltipService = new TooltipService(this.formGroup);
  }

  /**
   * set invoice in form
   */
  fillFormInvoice() {
    if (this.payment.invoices) {
      if (this.payment.invoices[0]) {
        return this.payment.invoices[0].id;
      }
    } else if (this.invoice) {
      this.invoices.push(this.invoice);
      return this.invoice.id;
    }
    return null;
  }

  /**
   * Check for draft payments
   * @param invoiceId
   * @param state
   */
  checkForDraftPayments(invoiceId, state) {
    this.restService.request<Payment[]>(RequestMethod.GET, ApiEndpoints.ACCOUNT.PAYMENT, `read/by_invoice?invoiceId=${invoiceId}&paymentState=${state}`).subscribe(
      response => {
        if (response.success && response.data) {
          if (response.data.length > 0) {
            this.invoiceDraftPayment = response.data[0];
          } else {
            this.invoiceDraftPayment = null;
          }
        }
      }
    );
  }

  /**
   * Set form payment amount
   */
  fillFormAmount() {
    if (this.payment.amount) {
      return this.payment.amount;
    } else if (this.invoice) {
      if (this.data.guestCheckout) {
        return {value: this.invoice.amountResidual, disabled: true};
      } else {
        return this.invoice.amountResidual;
      }
    }
    return null;
  }

  /**
   * Set form invoice param
   */

  /**
   * Cancel
   */
  cancel(): void {
    this.dialogRef.close({create: false, paymentMethod: null});
  }

  /**
   * Update journal
   * @param event
   */
  updateJournalevent(event) {
    const journal = this.journals.find(x => x.id === event.value);
    this.updateJournal(journal);
  }

  /**
   * Update payment amount
   */
  updateAmountToPay() {
    this.totalAmountToPay = this.formGroup.value.amount;
  }

  onTextareaValueChanged(newValue: string): void {
    this.charChange = newValue;
    this.formGroup.controls.description.setValue(this.charChange);
  }


  /**
   * Update journal
   * @param journal
   */
  updateJournal(journal) {
    if (journal.type === 'BANK') {
      this.isBankTransfer = true;
      this.formGroup.get('ref').setValidators(Validators.compose([Validators.required]));
      this.isStripe = false;
      this.formGroup.get('ref').enable();
    } else if (journal.type === 'STRIPE') { // Check for stripe and enable checkout tab
      this.isBankTransfer = false;
      this.isStripe = true;
      this.formGroup.get('ref').clearValidators();
      this.formGroup.get('ref').disable();
    } else {
      this.isBankTransfer = false;
      this.isStripe = false;
      this.formGroup.get('ref').clearValidators();
      this.formGroup.get('ref').disable();
    }
    reactiveFormValidator(this.formGroup.get('ref'));
  }

  /**
   * Get journals
   */
  getJournals() {
    const data = {
      fields: ['aj.id', 'aj.name', 'aj.type'],
      tables: ['account_journal aj'],
      conditions: [['deleted', 'IS', false]]
    };
    this.restService.request<PageableResponse<Journal>>(RequestMethod.POST, ApiEndpoints.ACCOUNT.JOURNAL, Actions.BASE_ACTIONS.READ + '/custom', {body: data}).subscribe(
      response => {
        if (response.success && response.data) {
          this.journals = response.data.items;
          if (this.journals.length > 0) {
            this.selectedJournal = this.journals[0].id;
            if (!this.data.paymentId && !this.data.invoiceId) {
              this.updateJournal(this.journals[0]);
            }
          }
          this.setJournalIcons();
        }
      });
  }

  /**
   * Journal icons
   */
  setJournalIcons() {
    for (const item of this.journals) {
      if (item.type === 'BANK') {
        item.icon = this.bankTransferIcon;
      } else if (item.type === 'STRIPE') {
        item.icon = this.creditCardIcon;
      }
    }
  }

  /**
   * Get invoices
   */
  getInvoices() {
    this.restService.request<PageableResponse<any>>(RequestMethod.GET, ApiEndpoints.ACCOUNT.INVOICE, `read/by_facility?facilityId=0&state=OPEN`)
      .subscribe(response => {
        if (response.success) {
          if (response.data && response.data.total > 0) {
            this.invoices = response.data.items.map(item => {
              item.partner = JSON.parse(item.partner);
              item.currency = JSON.parse(item.currency);
              return item;
            });
          }
          return;
        }
      });
  }

  /**
   * Get invoice
   */
  getInvoice() {
    this.restService.request<any>(RequestMethod.GET, ApiEndpoints.ACCOUNT.INVOICE, `read/${this.data.invoiceId}?invoiceId=${this.data.invoiceId}`)
      .subscribe(response => {
        if (response.success) {
          this.invoices = response.data;
          return;
        }
      });
  }

  /**
   * Create or update payment
   */
  createOrUpdatePayment() {
    if (!this.formGroup.valid) {
      return;
    }
    const formValues = this.formGroup.getRawValue();
    const invoiceTmp = this.invoices.find(invoice => invoice.id === formValues.invoice);
    this.payment.paymentType = PaymentTypeEnum.INBOUND;
    this.payment.invoices = [invoiceTmp];
    this.payment.currency = invoiceTmp.currency;
    this.payment.origin = invoiceTmp.number;
    this.payment.partner = invoiceTmp.partner;
    this.payment.journal = this.journals.find(journal => journal.id === formValues.journal);
    this.payment.description = this.charChange;
    if (formValues.amount < 0.5) {
      this.responseSnackBar.onResponseMessage('Minimum amount to pay is $0.5', GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
      this.isDisabled = false;
      return;
    }
    this.payment.amount = formValues.amount;
    this.payment.ref = formValues.ref;
    this.payment.company = this.company;
    if (this.data.paymentId) {
      this.update(this.payment);
    } else {
      this.create(this.payment);
    }
  }

  /**
   * Create stripe payment
   */
  createStripePayment() {
    if (!this.formGroup.valid) {
      this.tabgroup.selectedIndex = 0;
      return;
    }
    if (this.data.guestCheckout) {
      if (this.stripeCardToken) {
        this.createOrUpdatePayment();
      } else {
        this.responseSnackBar.onResponseMessage('Please select use card to pay', GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
      }
    } else {
      this.isDisabled = true;
      this.getAllCards(); // Check for cards
    }
  }

  /**
   * Create payment
   * @param item
   */
  create(item: Payment): void {
    let action = 'create';
    if (!this.data.guestCheckout) {
      action += '/payment';
    }
    if (this.stripeCardToken) {
      action += '?token=' + this.stripeCardToken;
      this.stripeCardToken = null;
    }
    this.restService.request<any>(
      RequestMethod.POST,
      ApiEndpoints.ACCOUNT.PAYMENT,
      action,
      {
        body: item
      }
    ).subscribe(response => {
      // this.responseSnackBar.onResponse(response);
      if (response && response.success) {
        if (response.data) {
          this.dialogRef.close({create: response.success, paymentMethod: response.data.journal.name});
        }
      } else {
        this.dialogRef.close({
          create: false,
          paymentMethod: this.journals.find(journal => journal.id === this.formGroup.value.journal).name
        });
      }
    });
  }

  /**
   * Update payment
   * @param item
   */
  update(item: Payment): void {
    this.restService.request<Payment>(
      RequestMethod.POST,
      ApiEndpoints.ACCOUNT.PAYMENT,
      'update',
      {
        body: item
      }
    ).subscribe(response => {
      // this.responseSnackBar.onResponse(response);
      this.dialogRef.close({create: true});
    });
  }

  /**
   * Stripe cards tab
   */
  stripeCheckout() {
    this.tabgroup.selectedIndex = 1;
  }

  /**
   * Update amount residual
   * @param event
   */
  updateAmount(event) {
    const invoice = this.invoices.find(x => x.id === event.value);
    this.checkForDraftPayments(invoice.id, 'DRAFT');
    this.updateAmountMax(invoice);
  }

  /**
   * Update amount max value
   * @param invoice
   */
  updateAmountMax(invoice) {
    this.invoiceResidual = invoice.amountResidual /*+ invoice.currency.symbol*/;
    this.formGroup.get('amount').setValidators(Validators.compose([Validators.max(invoice.amountResidual), Validators.required]));
    this.formGroup.get('amount').setValue(invoice.amountResidual);
  }

  /**
   * Get company data.
   */
  private getCompanyInfo() {
    const data = {
      fields: ['id', 'name', 'addressOne', 'salesEmail', 'phone', 'businessNumber', 'fiscalNumber', 'taxNumber', {city: ['id', 'name']}, {state: ['id', 'name']}, {country: ['id', 'name']}, {currency: ['id', 'name', 'symbol', 'position']}],
      conditions: [['id', '=', 1]],
      depth: 2
    };
    this.restService.request<PageableResponse<Company>>(RequestMethod.POST, ApiEndpoints.RES.COMPANY, Actions.BASE_ACTIONS.READ + '/fetch', {body: data}).subscribe(
      response => {
        if (response.success && response.data && response.data.items.length > 0) {
          this.company = response.data.items[0];
          if (this.company.id) {
            if (this.company.currency.position === 'before') {
              this.currencySymbolBefore = this.company.currency.symbol;
            } else {
              this.currencySymbolAfter = this.company.currency.symbol;
            }
          }
        }
      });

  }

  /**
   * Get Invoice Data
   */
  private getInvoiceData() {
    if (this.data.invoiceId) {
      this.restService.publicRequest<any>(RequestMethod.GET, ApiEndpoints.ACCOUNT.INVOICE_CHECKOUT, `read/by_id?invoiceId=${this.data.invoiceId}`).subscribe(
        response => {
          if (response.success && response.data) {
            this.invoice = response.data;
            this.initForm();
            this.updateAmountMax(this.invoice);
            this.updateJournal(this.journals[0]);
          }
        });
      this.checkForDraftPayments(this.data.invoiceId, 'DRAFT');
    }
  }

  /**
   * Get Payment Data
   */
  private getPaymentData() {
    if (this.data.paymentId) {
      this.restService.request<Payment>(RequestMethod.GET, ApiEndpoints.ACCOUNT.PAYMENT, `read/${this.data.paymentId}`).subscribe(
        response => {
          if (response.success && response.data) {
            this.payment = response.data;
            this.initForm();
            this.updateAmountMax(this.payment.invoices[0]);
            this.updateJournal(this.journals.find(x => x.id === this.payment.journal.id));
          }
        });
    }
  }

  /**
   * Get all stripe cards
   */
  private getAllCards(): void {
    const URL = `${environment.api}/${environment.apiVersion}/${ApiEndpoints.ACCOUNT.PAYMENT}/${Actions.BASE_ACTIONS.READ}/cards`;
    this.restService
      .restRequest<any>(
        RequestMethod.GET,
        URL
      )
      .subscribe(response => {
        if (response.success) {
          if (response.data.cards.length > 0) {
            this.createOrUpdatePayment();
          } else {
            this.responseSnackBar.onResponseMessage('Please add Card', GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
          }
        } else {
          this.isDisabled = false;
        }
      });
  }

}
